import { RefObject, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { useAppSelector } from '~/redux/hooks'
import { selectMenuIsExpanded, closeMenu } from '~/redux/slices/menu'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

export default function useShowTopbarOnScrolling<T extends HTMLElement>(topRef: RefObject<T | null>): void {
  const dispatch = useDispatch()
  const isMenuOpen = useAppSelector(selectMenuIsExpanded)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  // refs, not state: scrolling must not re-render the layout subtree on every scroll event
  const prevScrollposRef = useRef<number | undefined>(undefined)
  const lastTopBarPosRef = useRef<number | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    // anchor at open time, NOT on the first scroll event — a fast flick's first event lands tens of px
    // past the true origin, which made the menu hang for that distance before it started sliding
    lastTopBarPosRef.current = isMenuOpen ? Math.max(0, window.scrollY) : null

    const handleTopbarWhenMenuOpen = (scrollPos: number): void => {
      if (!topRef.current) return
      // anchor only moves up: scrolling above it re-bases where the slide-away starts
      const anchor = Math.min(lastTopBarPosRef.current ?? scrollPos, scrollPos)
      lastTopBarPosRef.current = anchor
      const maxMenuOpenRange = topRef.current.scrollHeight - 200

      if (scrollPos - anchor > maxMenuOpenRange) {
        dispatch(closeMenu())
        // the dismiss gesture scrolled the page by the whole panel height — jump back so the
        // user lands where they were when the menu opened, not deep in the page/footer
        window.scrollTo({ top: anchor })
        return
      }

      // no easing here: the open menu must track the scroll position 1:1 in BOTH directions, or it visibly lags/snaps
      topRef.current.style.transitionDuration = '0ms'
      topRef.current.style.top = `${Math.min(0, anchor - scrollPos + 1)}px`
    }

    const handleScroll = (): void => {
      // Safari elastic overscroll bounces scrollY below 0 — never let that poison the anchor
      const currentScrollPos = Math.max(0, window.scrollY)
      const isScrollingUp = prevScrollposRef.current && prevScrollposRef.current > currentScrollPos

      if (isMenuOpen) {
        handleTopbarWhenMenuOpen(currentScrollPos)
      } else if (isScrollingUp && topRef.current) {
        topRef.current.style.transitionDuration = '500ms'
        topRef.current.style.position = '-webkit-sticky'
        topRef.current.style.position = 'sticky'
        topRef.current.style.top = '0'
        lastTopBarPosRef.current = null
      } else if (topRef.current) {
        topRef.current.style.transitionDuration = '500ms'
        topRef.current.style.position = '-webkit-sticky'
        topRef.current.style.position = 'sticky'
        topRef.current.style.top = isMobile ? '-48px' : '-64px'
        lastTopBarPosRef.current = null
      }
      prevScrollposRef.current = currentScrollPos
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [dispatch, topRef, isMobile, isMenuOpen])
}
